import { SisternodeOutlined } from "@ant-design/icons";
import { Flex, Modal, Spin } from "antd";
import * as React from "react";
import { findNodeOwnerByKey } from "src/common/findNodeOwnerByKey";
import { onError } from "src/common/onError";
import { ZUid } from "src/types/zUid";
import {
  TreeActionsPort,
  TreeDataPort,
} from "src/pages/ManagementPage/objects/ports";
import { ValueO2, CommonNodeO2 } from "../../Obj2Nodes";
import { activateValueNodeO2 } from "../../utils/createMainTree";
import { SelectAttribute } from "../SelectAttribute";
import styles from "./AddExistingAttribute.module.less";

/**
 * Есть ограничение на то, какой атрибут может быть выбран
 * groupByDictionary
 * +--value1
 * |  +-- attr
 * +--value2
 *    +-- attr
 * То есть среди атрибутов, которые принадлежат значениям (кроме текущего) в текущей группе
 *
 * А еще нужно подгрузить эти значения
 */

interface PropsAddExistingAttribute {
  store: TreeActionsPort & TreeDataPort;
  valueNode: ValueO2;
}
export const AddExistingAttribute: React.FC<PropsAddExistingAttribute> = ({
  store,
  valueNode,
}) => {
  const [open, setOpen] = React.useState(false);
  const [attrId, setAttrId] = React.useState<ZUid | undefined>();
  const [tree, setTree] = React.useState<CommonNodeO2[]>([]);
  const [loading, setLoading] = React.useState(false);
  React.useEffect(() => {
    if (open) {
      setAttrId(undefined);
      setLoading(true);
      loadTree(valueNode, store.treeData)
        .then((topNodes) => setTree(topNodes))
        .finally(() => setLoading(false))
        .catch(onError);
    }
  }, [open]);
  const onChange = (newValue: ZUid | undefined) => {
    setAttrId(newValue);
  };
  const create = () => {
    setOpen(false);
    if (attrId) {
      store.addExistingAttr(valueNode, attrId);
    }
  };
  return (
    <>
      <Flex
        gap={8}
        align="center"
        onClick={() => setOpen(true)}
        className={styles.label}
      >
        <SisternodeOutlined className={styles.icon} />
        <span>Добавить существующий атрибут</span>
      </Flex>
      <Modal
        open={open}
        title="Выберите атрибут"
        onCancel={() => setOpen(false)}
        okButtonProps={{ disabled: !attrId }}
        onOk={create}
      >
        <Spin spinning={loading}>
          <SelectAttribute value={attrId} onChange={onChange} tree={tree} />
        </Spin>
      </Modal>
    </>
  );
};

const loadTree = async (
  curValueNode: ValueO2,
  allObjects: CommonNodeO2[],
): Promise<CommonNodeO2[]> => {
  const { owner } = findNodeOwnerByKey(curValueNode.key, allObjects) ?? {};
  if (!owner || owner.type !== "group") return [];
  const topNodes = (owner.children ?? []).filter(
    (node) => node.key !== curValueNode.key,
  );
  const unloadedNodes = topNodes.filter((n) => !n.children);
  if (unloadedNodes.length > 0) {
    await Promise.all(
      unloadedNodes.map((node) =>
        node.type === "value" ? activateValueNodeO2(node) : Promise.resolve(),
      ),
    );
  }
  return topNodes;
};
